import { useRef, useState } from 'react'
import { Maximize2 } from 'lucide-react'
import { GuardianExpoSection } from './GuardianExpoSubPageLayout'
import MarketMapFullscreen from './MarketMapFullscreen'

const ANIMATION_MS = 300

/**
 * 技能交換市集互動地圖：預覽圖點擊後開啟全螢幕地圖（可縮放/拖曳，直向裝置自動旋轉 90 度），
 * 在全螢幕地圖上點擊個別攤位熱區才會展開該攤位的詳情卡片（區域色＋分類代碼＋攤位全稱＋詳細內容）。
 * booths: [{ code, fullName, fullTitle, shortTitle, region, color, style: { left, top, width, height } }]（百分比定位）
 * labels: [{ text, style: { left, top } }]（純文字標籤，如出入口指示，不可點擊、無色塊）
 */
export default function MarketMapSection({ titleBlack, titleRed, mapSrc, mapAlt, booths, labels = [], last = false }) {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [selectedBooth, setSelectedBooth] = useState(null)
  const [originRect, setOriginRect] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const closeTimerRef = useRef(null)

  const handleBoothClick = (booth, e) => {
    clearTimeout(closeTimerRef.current)
    setOriginRect(e.currentTarget.getBoundingClientRect())
    setSelectedBooth(booth)
    // 下一輪再切到展開狀態，讓「從熱區原點出現」→「展開到最終大小」有過渡動畫可以跑
    requestAnimationFrame(() => requestAnimationFrame(() => setIsOpen(true)))
  }

  const handleClose = () => {
    setIsOpen(false)
    closeTimerRef.current = setTimeout(() => setSelectedBooth(null), ANIMATION_MS)
  }

  const cardStyle = (() => {
    if (!originRect) return undefined
    if (!isOpen) {
      return {
        position: 'fixed',
        left: '50%',
        top: '50%',
        width: '80%',
        aspectRatio: '1 / 1',
        transform: 'translate(-50%, -50%) scale(0.92)',
        opacity: 0,
        transition: `transform ${ANIMATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), opacity ${ANIMATION_MS}ms ease-out`,
      }
    }
    return {
      position: 'fixed',
      left: '50%',
      top: '50%',
      width: '80%',
      aspectRatio: '1 / 1',
      transform: 'translate(-50%, -50%) scale(1)',
      opacity: 1,
      transition: `transform ${ANIMATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), opacity ${ANIMATION_MS}ms ease-out`,
    }
  })()

  return (
    <GuardianExpoSection titleBlack={titleBlack} titleRed={titleRed} last={last}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsFullscreen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsFullscreen(true) }}
        className="relative self-stretch cursor-pointer"
        style={{ containerType: 'inline-size' }}
        aria-label={mapAlt}
      >
        <img src={mapSrc} alt={mapAlt} className="w-full h-auto block" style={{ borderRadius: 8 }} />
        {booths.map((booth) => (
          <div
            key={booth.code}
            className="absolute flex items-center justify-center text-center"
            style={{ ...booth.style, background: booth.color, borderRadius: 'clamp(2px, 0.4cqw, 4px)', padding: 'clamp(1px, 0.4cqw, 4px)' }}
          >
            <span
              className="line-clamp-4"
              style={{ color: 'white', fontFamily: 'Noto Sans TC', fontSize: 'clamp(4px, 1.2cqw, 18px)', fontWeight: 700, lineHeight: 1.25, whiteSpace: 'pre-line' }}
            >
              {booth.code}{'\n'}{booth.mapLabel}
            </span>
          </div>
        ))}
        {labels.map((label) => (
          <span
            key={label.text}
            className="absolute pointer-events-none flex flex-col items-center justify-center"
            style={{
              ...label.style,
              transform: 'translate(-50%, -50%)',
              color: label.boxed ? 'white' : '#000',
              fontFamily: 'Noto Sans TC',
              fontSize: 'clamp(8px, 1.2cqw, 16px)',
              fontWeight: 700,
              lineHeight: label.boxed ? 1.3 : undefined,
              ...(label.boxed
                ? {
                    background: '#595758',
                    border: '2px solid #171717',
                    borderRadius: 'clamp(3px, 0.4cqw, 6px)',
                    padding: 'clamp(1px, 0.7cqw, 8px) clamp(0px, 0.8cqw, 10px)',
                  }
                : {
                    borderBottom: '2px solid #000',
                    paddingBottom: 2,
                  }),
            }}
          >
            {label.text.split('\n').map((line, i) => (
              <span key={i} className="whitespace-nowrap">{line}</span>
            ))}
          </span>
        ))}
        <span
          className="absolute flex items-center justify-center"
          style={{ right: 12, bottom: 12, width: 36, height: 36, borderRadius: 9999, background: 'rgba(255,255,255,0.9)', boxShadow: '0px 2px 8px rgba(0,0,0,0.2)' }}
        >
          <Maximize2 size={18} className="text-[#3D3A36]" />
        </span>
      </div>

      {isFullscreen && (
        <MarketMapFullscreen
          mapSrc={mapSrc}
          mapAlt={mapAlt}
          booths={booths}
          labels={labels}
          onClose={() => setIsFullscreen(false)}
          onBoothClick={handleBoothClick}
        />
      )}

      {selectedBooth && (
        <div
          className="fixed inset-0 z-[10001] p-6"
          style={{ background: isOpen ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0)', transition: `background ${ANIMATION_MS}ms ease-out` }}
          onClick={handleClose}
        >
          <div
            className="overflow-hidden flex flex-col"
            style={{ borderRadius: 16, background: 'white', boxShadow: '0px 8px 32px rgba(0,0,0,0.24)', ...cardStyle }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between shrink-0 h-[25%]" style={{ padding: '16px 20px', background: selectedBooth.color, gap: 12 }}>
              <span className="line-clamp-1" style={{ color: 'white', fontFamily: 'Noto Serif TC', fontSize: 24, fontWeight: 700 }}>
                {selectedBooth.code} {selectedBooth.fullName}
              </span>
              <button
                onClick={handleClose}
                className="flex items-center justify-center shrink-0"
                style={{ width: 28, height: 28, borderRadius: 9999, background: 'rgba(255,255,255,0.24)', color: 'white' }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
              </button>
            </div>
            <div
              className="flex-1 flex flex-col items-center justify-center text-center"
              style={{ padding: 20, gap: 8 }}
            >
              <p style={{ color: '#171717', fontFamily: 'Noto Sans TC', fontSize: 28, fontWeight: 700, lineHeight: 1.25, textAlign: 'center', letterSpacing: 1.5,whiteSpace: 'pre-line' }}>
                {selectedBooth.fullTitle}
              </p>
            </div>
          </div>
        </div>
      )}
    </GuardianExpoSection>
  )
}
